import React, { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { useBeforeUnload } from "react-router-dom";
import { listSites } from "../api/sites";
import { listSignalMatchesBySite } from "../api/signalMatch";
import { listSignalTypes } from "../api/signalTypes";
import {
  listModbusHosts,
  listSignalRegisters,
  listModbusMappings,
  saveModbusHosts,
  saveSignalRegisters,
  saveModbusMappings,
} from "../api/modbus";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const DATA_FORMAT_OPTIONS = ["uint16", "int16", "int32", "uint32", "float32", "enum16"];
const ENDIAN_OPTIONS = ["ABCD", "BADC", "CDAB", "DCBA", "AB", "BA"];
const DEVICE_TYPE_OPTIONS = ["GENERIC", "HUAWEI", "SUNNER"];
const SUNNER_BLOCK_SIZE = 50;
const SUBREGISTER_OPTIONS = [
  { value: "full", label: "Full" },
  { value: "hi", label: "High byte" },
  { value: "lo", label: "Low byte" },
];
const SIGNAL_TYPE_DEFAULTS = {
  POUTINV: 41100,
  IRRAD: 40100,
};

const isSunnerDevice = (deviceType) => (deviceType || "").toUpperCase() === "SUNNER";

const effectiveSunnerStride = (value) => {
  if (value === "" || value == null) return SUNNER_BLOCK_SIZE;
  const stride = Number(value);
  return Number.isFinite(stride) && stride > 0 ? stride : SUNNER_BLOCK_SIZE;
};

const computeSunnerAddress = (baseRegister, localOffset, trackerId, addressStride) => {
  if (baseRegister === "" || baseRegister == null || trackerId === "" || trackerId == null) {
    return null;
  }
  const base = Number(baseRegister);
  const offset = localOffset === "" || localOffset == null ? 0 : Number(localOffset);
  const tracker = Math.max(Number(trackerId), 1);
  const stride = effectiveSunnerStride(addressStride);
  if (!Number.isFinite(base) || !Number.isFinite(offset) || !Number.isFinite(tracker)) {
    return null;
  }
  return base + offset + (stride * (tracker - 1));
};

function useUnsavedChangesPrompt(when) {
  useBeforeUnload(
    useCallback(
      (event) => {
        if (!when) return;
        event.preventDefault();
        event.returnValue = "";
      },
      [when]
    )
  );
}

const toMatchKey = (value) => (value === undefined || value === null ? "" : String(value));

const emptyRegister = (match, type) => ({
  id: null,
  signal_match_id: match.id,
  signal_type_id: match.signal_type_id,
  name: type?.name || match.signal_name,
  base_register: "",
  count: 2,
  data_format: "",
  endian: "",
  device_type: "",
  offset: "",
  address_stride: "",
  enum_map_text: "{}",
  enum_enabled: false,
  subregister: "full",
});

const buildRegisterDrafts = (matches, typesMap, rawRegisters) => {
  const byMatch = new Map();
  rawRegisters.forEach((reg) => {
    if (reg.signal_match_id) {
      byMatch.set(reg.signal_match_id, reg);
    }
  });
  const drafts = {};
  matches.forEach((match) => {
    const type = typesMap.get(match.signal_type_id);
    const reg = byMatch.get(match.id);
    const draft = reg
      ? {
          ...reg,
          base_register: reg.base_register ?? "",
          data_format: reg.data_format || "",
          endian: reg.endian || "",
          device_type: reg.device_type || "",
          offset: reg.offset ?? "",
          address_stride:
            reg.address_stride ?? ((reg.device_type || "").toUpperCase() === "SUNNER" ? String(SUNNER_BLOCK_SIZE) : ""),
          enum_enabled: !!reg.enum_map,
          enum_map_text: reg.enum_map ? JSON.stringify(reg.enum_map, null, 2) : "{}",
          subregister: reg.subregister || "full",
        }
      : emptyRegister(match, type);
    const defaultBase = SIGNAL_TYPE_DEFAULTS[type?.name_short || ""];
    if ((draft.base_register === "" || draft.base_register === null) && defaultBase) {
      draft.base_register = String(defaultBase);
    }
    drafts[match.id] = draft;
  });
  return drafts;
};

const buildMappingDrafts = (matches, rawMappings) => {
  const drafts = {};
  const byMatch = new Map();
  rawMappings.forEach((row) => byMatch.set(row.signal_match_id, row));
  matches.forEach((match) => {
    const mapping = byMatch.get(match.id);
    drafts[match.id] = mapping
      ? {
          ...mapping,
          slave_id: mapping.slave_id ?? "",
          offset: mapping.offset ?? "",
          bitmask: mapping.bitmask ?? "",
        }
      : {
          id: null,
          site_id: match.site_id,
          signal_match_id: match.id,
          signal_type_id: match.signal_type_id,
          host_id: null,
          signal_register_id: null,
          slave_id: "",
          offset: "",
          bitmask: "",
          multiplier: match.multiplier,
          poll_interval_s: 60,
          active: true,
        };
  });
  return drafts;
};

const createHostRow = (host) => ({
  tempId: `row-${host.id ?? Math.random().toString(36).slice(2, 9)}`,
  id: host.id ?? null,
  description: host.description || "",
  ip_address: host.ip_address || "",
  port: host.port ?? 502,
  scada_id: host.scada_id ?? "",
  active: host.active ?? true,
  disconnect_after_poll: host.disconnect_after_poll ?? false,
});

export default function ModbusMapping() {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");

  const [signalTypes, setSignalTypes] = useState([]);
  const signalTypeMap = useMemo(() => {
    const map = new Map();
    signalTypes.forEach((type) => map.set(type.id, type));
    return map;
  }, [signalTypes]);

  const [matches, setMatches] = useState([]);
  const [registers, setRegisters] = useState({});
  const [hosts, setHosts] = useState([]);
  const [mappings, setMappings] = useState({});

  const [registerDirty, setRegisterDirty] = useState(false);
  const [hostDirty, setHostDirty] = useState(false);
  const [mappingDirty, setMappingDirty] = useState(false);

  const [registerSaving, setRegisterSaving] = useState(false);
  const [hostSaving, setHostSaving] = useState(false);
  const [mappingSaving, setMappingSaving] = useState(false);

  const [selectedHostKey, setSelectedHostKey] = useState("");
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [modalTypes, setModalTypes] = useState([]);
  const [modalSignals, setModalSignals] = useState([]);
  const [modalError, setModalError] = useState("");
  const [enumModalOpen, setEnumModalOpen] = useState(false);
  const [enumModalSignalId, setEnumModalSignalId] = useState(null);
  const [enumModalDraft, setEnumModalDraft] = useState("{}");
  const [enumModalError, setEnumModalError] = useState("");

  const [typeFilters, setTypeFilters] = useState([]);
  const [nameFilter, setNameFilter] = useState("");

  const [pageError, setPageError] = useState("");

  const [loading, setLoading] = useState(false);

  const hasUnsaved = registerDirty || hostDirty || mappingDirty;
  useUnsavedChangesPrompt(hasUnsaved);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const [siteRows, typeRows] = await Promise.all([listSites(), listSignalTypes()]);
        setSites(siteRows || []);
        setSignalTypes(typeRows || []);
        if (siteRows && siteRows.length > 0) {
          setSelectedSite(siteRows[0].id);
        }
      } catch (err) {
        console.error("Failed to load base data", err);
        setPageError("Unable to load base datasets.");
      }
    };
    bootstrap();
  }, []);

  const selectedHostRow = hosts.find((row) => row.tempId === selectedHostKey);

  const refreshSiteData = useCallback(
    async (siteId) => {
      if (!siteId) return;
      setLoading(true);
      setPageError("");
      try {
        const [matchRows, hostRows, registerRows, mapRows] = await Promise.all([
          listSignalMatchesBySite(siteId),
          listModbusHosts(siteId),
          listSignalRegisters(siteId),
          listModbusMappings(siteId),
        ]);
        setMatches(matchRows || []);
        const hostState = (hostRows || []).map(createHostRow);
        setHosts(hostState);
        setSelectedHostKey(hostState[0]?.tempId ?? "");
        const registerDrafts = buildRegisterDrafts(matchRows || [], signalTypeMap, registerRows || []);
        setRegisters(registerDrafts);
        const mappingDrafts = buildMappingDrafts(matchRows || [], mapRows || []);
        setMappings(mappingDrafts);
        setRegisterDirty(false);
        setHostDirty(false);
        setMappingDirty(false);
        setModalTypes([]);
        setModalSignals([]);
        const availableShorts = new Set();
        (matchRows || []).forEach((row) => {
          const type = signalTypeMap.get(row.signal_type_id);
          if (type?.name_short) {
            availableShorts.add(type.name_short);
          }
        });
        setTypeFilters(Array.from(availableShorts));
      } catch (err) {
        console.error("Failed to load modbus data", err);
        setPageError("Unable to load Modbus data for the selected site.");
      } finally {
        setLoading(false);
      }
    },
    [signalTypeMap]
  );

  useEffect(() => {
    refreshSiteData(selectedSite);
  }, [selectedSite, refreshSiteData]);

  const matchById = useMemo(() => {
    const map = new Map();
    matches.forEach((match) => map.set(toMatchKey(match.id), match));
    return map;
  }, [matches]);

  const registerTypeEntries = useMemo(() => {
    const map = new Map();
    matches.forEach((match) => {
      const type = signalTypeMap.get(match.signal_type_id);
      if (!type) return;
      if (!map.has(type.id)) {
        map.set(type.id, { type, representativeMatchId: match.id });
      }
    });
    return Array.from(map.values());
  }, [matches, signalTypeMap]);

  const registerRows = useMemo(() => {
    const rows = registerTypeEntries.map(({ type, representativeMatchId }) => {
      const match = matchById.get(toMatchKey(representativeMatchId)) || {
        id: representativeMatchId,
        signal_type_id: type.id,
        signal_name: type.name,
        site_id: selectedSite,
        multiplier: 1,
      };
      const reg = registers[representativeMatchId] || emptyRegister(match, type);
      return {
        ...reg,
        signal_match_id: representativeMatchId,
        signal_type_id: type.id,
        signal_name: type.name,
        signal_type_code: type.name_short || "",
        multiplier: match?.multiplier ?? 1,
      };
    });
    return rows.sort((a, b) => (a.signal_type_code || "").localeCompare(b.signal_type_code || ""));
  }, [registerTypeEntries, matchById, registers, selectedSite]);

  const registerByType = useMemo(() => {
    const map = new Map();
    registerRows.forEach((row) => map.set(row.signal_type_id, row));
    return map;
  }, [registerRows]);

  const availableTypeFilters = useMemo(() => {
    const codes = new Set();
    matches.forEach((match) => {
      const type = signalTypeMap.get(match.signal_type_id);
      if (type?.name_short) {
        codes.add(type.name_short);
      }
    });
    return Array.from(codes).sort();
  }, [matches, signalTypeMap]);

  const filteredMatches = useMemo(() => {
    const filtered = matches.filter((match) => {
      const type = signalTypeMap.get(match.signal_type_id);
      const shortName = type?.name_short || "";
      if (typeFilters.length > 0 && !typeFilters.includes(shortName)) {
        return false;
      }
      if (nameFilter.trim()) {
        const query = nameFilter.trim().toLowerCase();
        const target = `${match.signal_name} ${match.source_name} ${shortName}`.toLowerCase();
        if (!target.includes(query)) return false;
      }
      return true;
    });
    filtered.sort((a, b) => {
      const nameA = (a.signal_name || a.source_name || "").toLowerCase();
      const nameB = (b.signal_name || b.source_name || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });
    return filtered;
  }, [matches, signalTypeMap, typeFilters, nameFilter]);

  const handleRegisterChange = (matchId, field, value) => {
    setRegisters((prev) => ({
      ...prev,
      [matchId]: {
        ...prev[matchId],
        [field]: value,
      },
    }));
    setRegisterDirty(true);
  };

  const openEnumModal = (row) => {
    if (!row?.enum_enabled) return;
    setEnumModalSignalId(row.signal_match_id);
    setEnumModalDraft(row.enum_map_text || "{}");
    setEnumModalError("");
    setEnumModalOpen(true);
  };

  const closeEnumModal = () => {
    setEnumModalOpen(false);
    setEnumModalSignalId(null);
    setEnumModalDraft("{}");
    setEnumModalError("");
  };

  const saveEnumModal = () => {
    const trimmed = enumModalDraft.trim();
    if (trimmed) {
      try {
        JSON.parse(trimmed);
      } catch (err) {
        setEnumModalError("Invalid enum JSON. Please fix the syntax before saving.");
        return;
      }
    }
    if (enumModalSignalId != null) {
      handleRegisterChange(enumModalSignalId, "enum_map_text", trimmed || "{}");
    }
    closeEnumModal();
  };

  const handleHostChange = (rowId, field, value) => {
    setHosts((prev) =>
      prev.map((row) =>
        row.tempId === rowId
          ? {
              ...row,
              [field]:
                field === "port"
                  ? parseInt(value || "0", 10) || ""
                  : field === "disconnect_after_poll"
                    ? Boolean(value)
                    : value,
            }
          : row
      )
    );
    setHostDirty(true);
  };

  const addHostRow = () => {
    setHosts((prev) => [...prev, createHostRow({})]);
    setHostDirty(true);
  };

  const handleMappingChange = (matchId, field, value) => {
    setMappings((prev) => {
      const matchInfo = matchById.get(toMatchKey(matchId));
      const current = prev[matchId] || {
        signal_match_id: matchId,
        signal_type_id: matchInfo?.signal_type_id,
      };
      const numericFields = new Set(["slave_id", "offset", "bitmask", "address_stride"]);
      const parsedValue =
        numericFields.has(field) && value !== "" && value !== null ? Number(value) : value;
      const updated = {
        ...current,
        [field]: parsedValue,
      };
      if (field === "host_id") {
        const register = registerByType.get(matchInfo?.signal_type_id);
        updated.signal_register_id = register?.id ?? null;
        updated.signal_type_id = matchInfo?.signal_type_id;
      }
      return {
        ...prev,
        [matchId]: updated,
      };
    });
    setMappingDirty(true);
  };

  const saveRegistersAction = async () => {
    setRegisterSaving(true);
    setPageError("");
    try {
      const missingFormat = registerRows.find((row) => !row.data_format || !row.endian);
      if (missingFormat) {
        setPageError(
          `Please choose both data format and endian for ${missingFormat.signal_name || missingFormat.signal_type_code}.`
        );
        setRegisterSaving(false);
        return;
      }
      const payload = [];
      for (const row of registerRows) {
        let enumMap = null;
        if (row.enum_enabled && row.enum_map_text && row.enum_map_text.trim()) {
          try {
            enumMap = JSON.parse(row.enum_map_text);
          } catch (err) {
            setPageError(`Invalid enum JSON for ${row.signal_name || row.signal_type_code}.`);
            setRegisterSaving(false);
            return;
          }
        }
        payload.push({
          id: row.id,
          signal_type_id: row.signal_type_id,
          signal_match_id: row.signal_match_id,
          name: row.name || row.signal_name,
          base_register: row.base_register === "" ? null : Number(row.base_register),
          count: Number(row.count) || 0,
          data_format: row.data_format || null,
          endian: row.endian || null,
          device_type: row.device_type || null,
          offset: row.offset === "" ? null : Number(row.offset),
          address_stride:
            row.address_stride === "" || row.address_stride == null ? null : Number(row.address_stride),
          enum_map: enumMap,
          subregister: row.subregister || null,
        });
      }
      const saved = await saveSignalRegisters(selectedSite, payload);
      const draft = buildRegisterDrafts(matches, signalTypeMap, saved);
      setRegisters(draft);
      setRegisterDirty(false);
    } catch (err) {
      console.error("Failed to save registers", err);
      setPageError("Unable to save Modbus signal registers.");
    } finally {
      setRegisterSaving(false);
    }
  };

  const saveHostsAction = async () => {
    setHostSaving(true);
    setPageError("");
    try {
      const payload = hosts.map((row) => ({
        id: row.id,
        description: row.description,
        ip_address: row.ip_address,
        port: row.port === "" ? 502 : Number(row.port),
        scada_id: row.scada_id === "" ? null : Number(row.scada_id),
        active: row.active,
        disconnect_after_poll: Boolean(row.disconnect_after_poll),
      }));
      const saved = await saveModbusHosts(selectedSite, payload);
      const updated = saved.map(createHostRow);
      setHosts(updated);
      if (updated.length > 0) {
        setSelectedHostKey(updated[0].tempId);
      }
      setHostDirty(false);
    } catch (err) {
      console.error("Failed to save hosts", err);
      setPageError("Unable to save Modbus hosts.");
    } finally {
      setHostSaving(false);
    }
  };

  const saveMappingsAction = async () => {
    setMappingSaving(true);
    setPageError("");
    try {
      const payload = Object.values(mappings)
        .filter(Boolean)
        .map((row) => ({
          id: row.id,
          host_id: row.host_id,
          signal_register_id: row.signal_register_id,
          signal_type_id: row.signal_type_id,
          signal_match_id: row.signal_match_id,
          array_inverter_id: row.array_inverter_id,
          slave_id: row.slave_id === "" ? null : Number(row.slave_id),
          multiplier: row.multiplier,
          poll_interval_s: row.poll_interval_s,
          active: row.active,
          offset: row.offset === "" ? null : Number(row.offset),
          bitmask: row.bitmask === "" ? null : Number(row.bitmask),
        }));
      const saved = await saveModbusMappings(selectedSite, payload);
      const draft = buildMappingDrafts(matches, saved);
      setMappings(draft);
      setMappingDirty(false);
    } catch (err) {
      console.error("Failed to save mappings", err);
      setPageError("Unable to save Modbus signal mappings.");
    } finally {
      setMappingSaving(false);
    }
  };

  const openMapModal = () => {
    if (!selectedHostRow || !selectedHostRow.id) {
      setPageError("Please save and select a Modbus host before mapping.");
      return;
    }
    setModalTypes([]);
    setModalSignals([]);
    setModalError("");
    setMapModalOpen(true);
  };

  const applyModalMapping = () => {
    if (!selectedHostRow?.id) {
      setModalError("Please pick and save a host first.");
      return;
    }
    if (modalSignals.length === 0) {
      setModalError("Select at least one signal.");
      return;
    }

    const missingType = modalSignals.some((signalId) => {
      const matchInfo = matchById.get(toMatchKey(signalId));
      if (!matchInfo) return true;
      const register = registerByType.get(matchInfo.signal_type_id);
      return !register?.id;
    });
    if (missingType) {
      setModalError("Save register data for the selected signal types before mapping hosts.");
      return;
    }
    setMappings((prev) => {
      const updated = { ...prev };
      modalSignals.forEach((signalId) => {
        const matchInfo = matchById.get(toMatchKey(signalId));
        if (!matchInfo) return;
        const register = registerByType.get(matchInfo.signal_type_id);
        updated[signalId] = {
          ...(prev[signalId] || {}),
          signal_match_id: signalId,
          signal_type_id: matchInfo.signal_type_id,
          host_id: selectedHostRow.id,
          signal_register_id: register?.id ?? null,
        };
      });
      return updated;
    });
    setMappingDirty(true);
    setMapModalOpen(false);
  };

  const availableSignalsForModal = useMemo(() => {
    const filtered = matches.filter((match) => {
      const type = signalTypeMap.get(match.signal_type_id);
      const code = type?.name_short || "";
      if (modalTypes.length === 0) return true;
      return modalTypes.includes(code);
    });
    filtered.sort((a, b) => {
      const labelA = (a.signal_name || a.source_name || `Signal ${a.id}`).toLowerCase();
      const labelB = (b.signal_name || b.source_name || `Signal ${b.id}`).toLowerCase();
      return labelA.localeCompare(labelB);
    });
    return filtered;
  }, [matches, signalTypeMap, modalTypes]);

  const stickyClasses = "sticky top-0 bg-slate-50/95 z-20 border-b border-slate-200 backdrop-blur";

  return (
    <div className="space-y-6">
      <div className={stickyClasses}>
        <div className="flex flex-wrap items-center gap-4 px-4 py-3">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Modbus mapping</h1>
            <p className="text-sm text-slate-500">
              Configure Modbus signal registers, hosts, and per-signal mappings for SCADA ingestion.
            </p>
          </div>
          <div className="ml-auto">
            <label className="text-xs font-semibold text-slate-500 block mb-1">Select site</label>
            <select
              value={selectedSite}
              onChange={(event) => setSelectedSite(Number(event.target.value))}
              className="rounded-md border px-3 py-2 text-sm bg-white"
            >
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        {pageError && (
          <div className="px-4 pb-3 text-sm text-red-800">
            <div className="rounded-md bg-red-50 px-3 py-2">{pageError}</div>
          </div>
        )}
      </div>

      <section className="rounded-2xl bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Modbus signal types</h2>
            <p className="text-xs text-slate-500">
              Map SCADA signal matches to Modbus register definitions for this site.
            </p>
          </div>
          <Button disabled={registerSaving || loading} onClick={saveRegistersAction}>
            {registerSaving ? "Saving..." : "Save"}
          </Button>
        </div>
        <div className="mt-4 max-h-48 overflow-hidden rounded-lg border">
          <div className="max-h-48 overflow-y-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="sticky top-0 bg-slate-100 text-xs uppercase text-slate-500">
                <tr>
                  <th className="w-64 px-3 py-2">Signal name</th>
                  <th className="px-3 py-2">Signal type</th>
                  <th className="px-3 py-2">Device type</th>
                  <th className="w-20 px-3 py-2">Multiplier</th>
                  <th className="px-3 py-2">Base register</th>
                  <th className="px-3 py-2">Count</th>
                  <th className="px-3 py-2">Sub register</th>
                  <th className="px-3 py-2">Data format</th>
                  <th className="px-3 py-2">Endian</th>
                  <th className="px-3 py-2">Offset</th>
                  <th className="px-3 py-2">Stride</th>
                  <th className="w-80 px-3 py-2">Enum</th>
                </tr>
              </thead>
              <tbody>
                {registerRows.map((row) => (
                  <tr key={row.signal_match_id} className="odd:bg-white even:bg-slate-50">
                    <td className="w-64 px-3 py-2 font-medium">
                      <div className="max-w-[220px] truncate" title={row.signal_name}>
                        {row.signal_name}
                      </div>
                    </td>
                    <td className="px-3 py-2">{row.signal_type_code}</td>
                    <td className="w-32 px-3 py-2">
                      <select
                        value={row.device_type || ""}
                        onChange={(event) =>
                          handleRegisterChange(row.signal_match_id, "device_type", event.target.value)
                        }
                        className="w-full rounded-md border px-2 py-2 text-sm"
                      >
                        <option value="">Select</option>
                        {DEVICE_TYPE_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="w-20 px-3 py-2">{row.multiplier}</td>
                    <td className="w-32 px-3 py-2">
                      <Input
                        value={row.base_register ?? ""}
                        onChange={(event) =>
                          handleRegisterChange(row.signal_match_id, "base_register", event.target.value)
                        }
                      />
                    </td>
                    <td className="w-24 px-3 py-2">
                      <Input
                        type="number"
                        value={row.count}
                        onChange={(event) =>
                          handleRegisterChange(row.signal_match_id, "count", event.target.value)
                        }
                      />
                    </td>
                    <td className="w-28 px-3 py-2">
                      <select
                        value={row.subregister || "full"}
                        onChange={(event) =>
                          handleRegisterChange(row.signal_match_id, "subregister", event.target.value)
                        }
                        className="w-full rounded-md border px-2 py-2 text-sm"
                      >
                        {SUBREGISTER_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="w-32 px-3 py-2">
                      <select
                        value={row.data_format || ""}
                        onChange={(event) =>
                          handleRegisterChange(row.signal_match_id, "data_format", event.target.value)
                        }
                        className="w-full rounded-md border px-2 py-2 text-sm"
                      >
                        <option value="">Select</option>
                        {DATA_FORMAT_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="w-28 px-3 py-2">
                      <select
                        value={row.endian || ""}
                        onChange={(event) =>
                          handleRegisterChange(row.signal_match_id, "endian", event.target.value)
                        }
                        className="w-full rounded-md border px-2 py-2 text-sm"
                      >
                        <option value="">Select</option>
                        {ENDIAN_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="w-24 px-3 py-2">
                      <Input
                        type="number"
                        value={row.offset ?? ""}
                        onChange={(event) =>
                          handleRegisterChange(row.signal_match_id, "offset", event.target.value)
                        }
                      />
                    </td>
                    <td className="w-24 px-3 py-2">
                      <Input
                        type="number"
                        value={row.address_stride ?? ""}
                        onChange={(event) =>
                          handleRegisterChange(row.signal_match_id, "address_stride", event.target.value)
                        }
                        placeholder={isSunnerDevice(row.device_type) ? String(SUNNER_BLOCK_SIZE) : ""}
                      />
                    </td>
                    <td className="w-80 px-3 py-2">
                      <div className="flex items-center gap-3 whitespace-nowrap">
                        <label className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            checked={!!row.enum_enabled}
                            onChange={(event) =>
                              {
                                const enabled = event.target.checked;
                                handleRegisterChange(row.signal_match_id, "enum_enabled", enabled);
                                if (!enabled && enumModalSignalId === row.signal_match_id) {
                                  closeEnumModal();
                                }
                              }
                            }
                          />
                          <span>Use enumeration</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => openEnumModal(row)}
                          disabled={!row.enum_enabled}
                          className="rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Edit enum
                        </button>
                        <span className="truncate text-xs text-slate-500">
                          {row.enum_enabled
                            ? row.enum_map_text && row.enum_map_text.trim() && row.enum_map_text.trim() !== "{}"
                              ? "Enumeration configured"
                              : "No enum values configured yet"
                            : "Enable enumeration to edit"}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <Dialog open={enumModalOpen} onOpenChange={(open) => (open ? null : closeEnumModal())}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Edit enum
              {enumModalSignalId != null
                ? ` — ${registerRows.find((row) => row.signal_match_id === enumModalSignalId)?.signal_name || ""}`
                : ""}
            </DialogTitle>
            <p className="text-sm text-slate-500">
              Enter a JSON object that maps raw values to labels.
            </p>
          </DialogHeader>
          {enumModalError ? (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{enumModalError}</div>
          ) : null}
          <textarea
            rows={16}
            value={enumModalDraft}
            onChange={(event) => setEnumModalDraft(event.target.value)}
            className="w-full rounded-md border px-3 py-3 text-xs font-mono text-slate-900"
            placeholder={'{\n  "0x0000": {\n    "label": "Standby"\n  }\n}'}
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={closeEnumModal}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <Button type="button" onClick={saveEnumModal}>
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <section className="rounded-2xl bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Modbus hosts</h2>
                <p className="text-xs text-slate-500">Configure host endpoints used to poll Modbus signals.</p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={addHostRow}
                  className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Add host
                </button>
                <Button disabled={hostSaving || loading} onClick={saveHostsAction}>
                  {hostSaving ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
            <div className="mt-4 max-h-52 overflow-hidden rounded-lg border">
              <div className="max-h-52 overflow-y-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="sticky top-0 bg-slate-100 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Description</th>
                      <th className="px-3 py-2">IP Address</th>
                      <th className="px-3 py-2">Port</th>
                      <th className="px-3 py-2">SCADA ID</th>
                      <th className="px-3 py-2">Disconnect After Poll</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hosts.map((row) => (
                      <tr
                        key={row.tempId}
                        className={clsx(
                          "cursor-pointer odd:bg-white even:bg-slate-50",
                          row.tempId === selectedHostKey && "bg-blue-50"
                        )}
                        onClick={() => setSelectedHostKey(row.tempId)}
                      >
                        <td className="px-3 py-2">
                          <Input
                            value={row.description}
                            onChange={(event) => handleHostChange(row.tempId, "description", event.target.value)}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            value={row.ip_address}
                            onChange={(event) => handleHostChange(row.tempId, "ip_address", event.target.value)}
                            placeholder="192.168.0.10"
                          />
                        </td>
                        <td className="w-24 px-3 py-2">
                          <Input
                            type="number"
                            value={row.port}
                            onChange={(event) => handleHostChange(row.tempId, "port", event.target.value)}
                          />
                        </td>
                        <td className="w-28 px-3 py-2">
                          <Input
                            type="number"
                            value={row.scada_id}
                            onChange={(event) => handleHostChange(row.tempId, "scada_id", event.target.value)}
                          />
                        </td>
                        <td className="w-48 px-3 py-2">
                          <label className="flex items-center gap-2 text-sm text-slate-700">
                            <input
                              type="checkbox"
                              checked={Boolean(row.disconnect_after_poll)}
                              onChange={(event) =>
                                handleHostChange(row.tempId, "disconnect_after_poll", event.target.checked)
                              }
                            />
                            <span>Close socket after host poll</span>
                          </label>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                onClick={openMapModal}
                disabled={!selectedHostRow || !selectedHostRow.id}
                className="rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                + Map host to signals
              </button>
              {selectedHostRow && !selectedHostRow.id && (
                <span className="text-xs text-slate-500">Save the host first before mapping.</span>
              )}
            </div>
          </section>

          <section className="rounded-2xl bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center gap-3">
              <div>
                <h2 className="text-lg font-semibold">Signal filters</h2>
                <p className="text-xs text-slate-500">Filter signal cards before applying mapping changes.</p>
              </div>
              <Button className="ml-auto" disabled={mappingSaving || loading} onClick={saveMappingsAction}>
                {mappingSaving ? "Saving..." : "Save mappings"}
              </Button>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <div className="flex flex-wrap gap-2">
                {availableTypeFilters.map((code) => {
                  const active = typeFilters.includes(code);
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() =>
                        setTypeFilters((prev) =>
                          active ? prev.filter((item) => item !== code) : [...prev, code]
                        )
                      }
                      className={clsx(
                        "rounded-full border px-3 py-1 text-xs font-semibold",
                        active ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600"
                      )}
                    >
                      {code}
                    </button>
                  );
                })}
              </div>
              <div className="ml-auto w-64">
                <Input
                  placeholder="Filter by signal name..."
                  value={nameFilter}
                  onChange={(event) => setNameFilter(event.target.value)}
                />
              </div>
            </div>
          </section>

      <section className="space-y-4">
        {loading && <div className="text-sm text-slate-500">Loading data...</div>}
        {!loading && filteredMatches.length === 0 && (
          <div className="rounded-md bg-slate-100 px-4 py-6 text-center text-slate-600">
            No signals match the current filters.
          </div>
        )}
        {filteredMatches.map((match) => {
          const type = signalTypeMap.get(match.signal_type_id) || {};
          const mapping = mappings[match.id];
          const registerDef = registerByType.get(match.signal_type_id);
          const isSunner = isSunnerDevice(registerDef?.device_type);
          const selectedHost = hosts.find((row) => row.id === mapping?.host_id);
          const trackerId = mapping?.slave_id ?? "";
          const localOffset =
            mapping?.offset !== "" && mapping?.offset != null
              ? mapping.offset
              : registerDef?.offset ?? "";
          const computedSunnerAddress = isSunner
            ? computeSunnerAddress(
                registerDef?.base_register,
                localOffset,
                trackerId,
                registerDef?.address_stride
              )
            : null;
          const sunnerStride = effectiveSunnerStride(registerDef?.address_stride);
          const signalUnitHint =
            selectedHost?.scada_id === "" || selectedHost?.scada_id == null ? 1 : Number(selectedHost.scada_id);
          return (
            <div key={match.id} className="rounded-3xl bg-white p-5 shadow-sm border border-slate-100">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1">
                  <div className="text-lg font-semibold text-slate-900">{match.signal_name}</div>
                  <div className="text-sm text-slate-500">{match.source_name}</div>
                </div>
                <div className="flex items-center gap-2 text-xs uppercase">
                  <span className="rounded-full bg-slate-100 px-3 py-1 font-semibold">{type.name_short}</span>
                  <span className="rounded-full bg-slate-100 px-3 py-1 font-semibold">
                    Multiplier {match.multiplier}
                  </span>
                  {registerDef?.device_type && (
                    <span className="rounded-full bg-amber-100 px-3 py-1 font-semibold text-amber-800">
                      {registerDef.device_type}
                    </span>
                  )}
                </div>
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-3">
                <div>
                  <label className="text-xs font-semibold text-slate-500 block mb-1">Modbus host</label>
                  <select
                    className="w-full rounded-md border px-3 py-2 text-sm"
                    value={mapping?.host_id || ""}
                    onChange={(event) =>
                      handleMappingChange(match.id, "host_id", event.target.value ? Number(event.target.value) : null)
                    }
                  >
                    <option value="">Select host</option>
                    {hosts
                      .filter((row) => row.id)
                      .map((row) => (
                        <option key={row.id} value={row.id}>
                          {row.description || row.ip_address}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 block mb-1">
                    {isSunner ? "Tracker ID" : "SCADA ID"}
                  </label>
                  <Input
                    type="number"
                    value={mapping?.slave_id ?? ""}
                    onChange={(event) =>
                      handleMappingChange(match.id, "slave_id", event.target.value)
                    }
                    placeholder={isSunner ? "Tracker number (e.g. 1)" : ""}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 block mb-1">
                    {isSunner ? "Local offset" : "Offset"}
                  </label>
                  <Input
                    type="number"
                    value={mapping?.offset ?? ""}
                    onChange={(event) => handleMappingChange(match.id, "offset", event.target.value)}
                    placeholder={isSunner ? "Local signal offset (optional)" : "Offset (e.g. 50)"}
                  />
                </div>
              </div>
              <div className={clsx("mt-4 grid gap-4", isSunner ? "md:grid-cols-3" : "md:grid-cols-1")}>
                <div>
                  <label className="text-xs font-semibold text-slate-500 block mb-1">Bitmask</label>
                  <Input
                    type="text"
                    value={mapping?.bitmask ?? ""}
                    onChange={(event) => handleMappingChange(match.id, "bitmask", event.target.value)}
                    placeholder="0xF000 or decimal"
                  />
                </div>
                {isSunner && (
                  <div>
                    <label className="text-xs font-semibold text-slate-500 block mb-1">Computed register</label>
                    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 min-h-10 flex items-center">
                      {computedSunnerAddress ?? "Enter a tracker ID to preview the register address."}
                    </div>
                  </div>
                )}
                {isSunner && (
                  <div className="flex flex-col justify-end gap-1 text-xs text-slate-500">
                    <p>Uses NCU unit `{signalUnitHint}` from the selected host.</p>
                    <p>
                      Effective address = base + local offset + {sunnerStride} × (tracker ID - 1).
                    </p>
                    {registerDef?.offset != null && registerDef?.offset !== "" && mapping?.offset === "" && (
                      <p>Blank local offset falls back to the signal type default of {registerDef.offset}.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </section>

      <Dialog open={mapModalOpen} onOpenChange={setMapModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Map host to signals</DialogTitle>
            {selectedHostRow?.description && (
              <p className="text-sm text-slate-500">Host: {selectedHostRow.description}</p>
            )}
          </DialogHeader>
          {modalError && <div className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{modalError}</div>}
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Signal types</label>
              <select
                multiple
                value={modalTypes}
                onChange={(event) =>
                  setModalTypes(Array.from(event.target.selectedOptions, (option) => option.value))
                }
                className="h-32 w-full rounded-md border px-3 py-2 text-sm bg-white text-slate-800"
              >
                {availableTypeFilters.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-500">Hold Ctrl/Cmd to pick multiple entries.</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Signals</label>
              <select
                multiple
                value={modalSignals.map(String)}
                onChange={(event) =>
                  setModalSignals(Array.from(event.target.selectedOptions, (option) => Number(option.value)))
                }
                className="h-48 w-full rounded-md border px-3 py-2 text-sm bg-white text-slate-800"
              >
                {availableSignalsForModal.map((signal) => {
                  const type = signalTypeMap.get(signal.signal_type_id);
                  const label =
                    signal.signal_name ||
                    signal.source_name ||
                    type?.name ||
                    `Signal ${signal.id}`;
                  return (
                    <option key={signal.id} value={signal.id}>
                      {label}
                    </option>
                  );
                })}
              </select>
              <p className="mt-1 text-xs text-slate-500">
                The list is filtered by the selected signal types. Hold Ctrl/Cmd for multi-select.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setMapModalOpen(false)}
                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <Button type="button" onClick={applyModalMapping}>
                Apply
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
